import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { 
  Building2,
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Home,
  Users,
  PieChart,
  BarChart3,
  ArrowRight,
  RefreshCw,
  Wallet,
  Landmark,
  BookOpen,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Bell,
  FileText,
  Bot,
  Sparkles,
  CreditCard,
  Percent
} from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatCurrencyFull = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value || 0);
};

export default function RentDashboard() {
  const { api } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [leaseAlerts, setLeaseAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [debtAnalysis, setDebtAnalysis] = useState(null);
  const [debtLoading, setDebtLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, alertsRes] = await Promise.all([
        api.get(`/ledger/portfolio-dashboard?start_date=${startDate}&end_date=${endDate}`),
        api.get('/ledger/lease-alerts?days_ahead=90')
      ]);
      setDashboardData(dashRes.data);
      setLeaseAlerts(alertsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [api, startDate, endDate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const runDebtAnalysis = async () => {
    setDebtLoading(true);
    toast.info("🔄 Analyzing debt reduction strategies...");
    try {
      const res = await api.post("/ai/debt-reduction");
      setDebtAnalysis(res.data);
      if (res.data.analysis) {
        toast.success("✅ Debt analysis complete!");
      }
    } catch (error) {
      toast.error("Failed to analyze debt");
    } finally {
      setDebtLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!dashboardData) {
    return (
      <Layout>
        <div className="text-center py-12 text-zinc-500">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No data available</p>
        </div>
      </Layout>
    );
  }

  const { summary: portfolio_summary = {}, properties = [], monthly_trend = [] } = dashboardData || {};

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              Rental Portfolio Dashboard
            </h1>
            <p className="text-zinc-500 mt-1">
              Overview of all properties and rental income
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-zinc-400 text-sm">From:</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-black/50 border-white/10 rounded-sm w-36"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-zinc-400 text-sm">To:</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-black/50 border-white/10 rounded-sm w-36"
              />
            </div>
            <Button onClick={fetchDashboard} variant="outline" className="border-white/10 rounded-sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={runDebtAnalysis} 
              disabled={debtLoading}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-sm"
              data-testid="debt-reduction-btn"
            >
              {debtLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Analyzing...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4 mr-2" />
                  🧠 AI Debt Reduction
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Lease Expiration Alerts */}
        {leaseAlerts?.summary?.total_alerts > 0 && (
          <Card className="bg-[#0A0A0A] border-orange-500/30 rounded-none" data-testid="lease-alerts-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Bell className="w-5 h-5 text-orange-400" />
                  Lease Expiration Alerts
                </CardTitle>
                <div className="flex items-center gap-3 text-xs">
                  {leaseAlerts?.summary?.expired > 0 && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded">
                      {leaseAlerts.summary.expired} Expired
                    </span>
                  )}
                  {leaseAlerts?.summary?.critical > 0 && (
                    <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded">
                      {leaseAlerts.summary.critical} Critical (≤30 days)
                    </span>
                  )}
                  {leaseAlerts?.summary?.warning > 0 && (
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
                      {leaseAlerts.summary.warning} Warning (≤60 days)
                    </span>
                  )}
                  {leaseAlerts?.summary?.notice > 0 && (
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                      {leaseAlerts.summary.notice} Notice (≤90 days)
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(leaseAlerts?.alerts || []).slice(0, 6).map((alert) => {
                  const urgencyStyles = {
                    expired: "border-red-500/50 bg-red-500/5",
                    critical: "border-orange-500/50 bg-orange-500/5",
                    warning: "border-yellow-500/50 bg-yellow-500/5",
                    notice: "border-blue-500/50 bg-blue-500/5"
                  };
                  const urgencyIcons = {
                    expired: <XCircle className="w-4 h-4 text-red-400" />,
                    critical: <AlertTriangle className="w-4 h-4 text-orange-400" />,
                    warning: <Clock className="w-4 h-4 text-yellow-400" />,
                    notice: <Bell className="w-4 h-4 text-blue-400" />
                  };
                  
                  return (
                    <div 
                      key={alert.unit_id} 
                      className={`p-3 border rounded-sm ${urgencyStyles[alert.urgency]}`}
                      data-testid={`lease-alert-${alert.unit_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {urgencyIcons[alert.urgency]}
                          <div>
                            <p className="font-medium text-white text-sm">
                              Unit {alert.unit_number}
                            </p>
                            <p className="text-xs text-zinc-500">{alert.property_name}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          alert.urgency === 'expired' ? 'bg-red-500/20 text-red-400' :
                          alert.urgency === 'critical' ? 'bg-orange-500/20 text-orange-400' :
                          alert.urgency === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {alert.urgency_label}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-zinc-400">
                          <span className="text-zinc-500">Tenant:</span> {alert.tenant_name || "—"}
                        </p>
                        <p className="text-xs text-zinc-400">
                          <span className="text-zinc-500">Rent:</span> {formatCurrency(alert.rent_amount)}/mo
                        </p>
                        <p className="text-xs text-zinc-400">
                          <span className="text-zinc-500">Lease End:</span> {alert.lease_end}
                        </p>
                      </div>
                      <div className="mt-2 pt-2 border-t border-white/5 flex gap-2">
                        <Link 
                          to={`/real-estate/${alert.property_id}/unit/${alert.unit_id}/ledger`}
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          View Ledger
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
              {(leaseAlerts?.alerts?.length || 0) > 6 && (
                <p className="text-center text-xs text-zinc-500 mt-3">
                  +{(leaseAlerts?.alerts?.length || 0) - 6} more alerts
                </p>
              )}
              <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-zinc-500">
                <span>
                  At-Risk Monthly Rent: <span className="text-orange-400 font-mono">{formatCurrency(leaseAlerts?.summary?.total_at_risk_rent || 0)}</span>
                </span>
                <span>
                  At-Risk Deposits: <span className="text-orange-400 font-mono">{formatCurrency(leaseAlerts?.summary?.total_at_risk_deposits || 0)}</span>
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Debt Reduction Analysis */}
        {debtAnalysis && (
          <Card className="bg-[#0A0A0A] border-emerald-500/30 rounded-none" data-testid="debt-analysis-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  AI Debt Reduction Strategy
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setDebtAnalysis(null)}
                  className="text-zinc-500 hover:text-white"
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              {debtAnalysis.properties_analyzed && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded">
                    <p className="text-xs text-emerald-400">Properties Analyzed</p>
                    <p className="text-xl font-bold text-white">{debtAnalysis.properties_analyzed}</p>
                  </div>
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                    <p className="text-xs text-blue-400">Total Debt</p>
                    <p className="text-xl font-bold text-white">{formatCurrency(debtAnalysis.total_debt)}</p>
                  </div>
                  <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded">
                    <p className="text-xs text-violet-400">Monthly Payments</p>
                    <p className="text-xl font-bold text-white">{formatCurrency(debtAnalysis.monthly_debt_payments)}</p>
                  </div>
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                    <p className="text-xs text-yellow-400">Avg Interest Rate</p>
                    <p className="text-xl font-bold text-white">{debtAnalysis.avg_interest_rate?.toFixed(2)}%</p>
                  </div>
                </div>
              )}
              {debtAnalysis.analysis && (
                <div className="p-4 bg-black/30 border border-emerald-500/20 rounded max-h-96 overflow-y-auto">
                  <p className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-3">AI Recommendations</p>
                  <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                    {debtAnalysis.analysis}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Portfolio Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-400 uppercase tracking-wide">Total Income</p>
                  <p className="text-2xl font-bold text-green-400 font-mono mt-1">
                    {formatCurrency(portfolio_summary?.total_income || 0)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">{portfolio_summary?.transaction_count || 0} transactions</p>
                </div>
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-500/20 to-red-600/10 border-red-500/30 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-400 uppercase tracking-wide">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-400 font-mono mt-1">
                    {formatCurrency(portfolio_summary?.total_expenses || 0)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">Across all properties</p>
                </div>
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-primary/20 to-primary/10 border-primary/30 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-primary uppercase tracking-wide">Net Profit</p>
                  <p className={`text-2xl font-bold font-mono mt-1 ${
                    (portfolio_summary?.net_profit || 0) >= 0 ? "text-primary" : "text-red-400"
                  }`}>
                    {formatCurrency(portfolio_summary?.net_profit || 0)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {(portfolio_summary?.net_profit || 0) >= 0 ? "Positive cash flow" : "Negative cash flow"}
                  </p>
                </div>
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-400 uppercase tracking-wide">Portfolio Equity</p>
                  <p className="text-2xl font-bold text-blue-400 font-mono mt-1">
                    {formatCurrency(portfolio_summary?.total_equity || 0)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Value: {formatCurrency(portfolio_summary?.total_property_value || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Landmark className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Second Row - Portfolio Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-zinc-400">Properties</p>
                <Building2 className="w-5 h-5 text-zinc-500" />
              </div>
              <p className="text-3xl font-bold text-white">{portfolio_summary?.property_count || 0}</p>
              <p className="text-xs text-zinc-500 mt-1">
                Total mortgage: {formatCurrency(portfolio_summary?.total_mortgage || 0)}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-zinc-400">Units</p>
                <Home className="w-5 h-5 text-zinc-500" />
              </div>
              <p className="text-3xl font-bold text-white">{portfolio_summary?.total_units || 0}</p>
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-zinc-500">Occupancy Rate</span>
                  <span className="text-primary">{portfolio_summary?.occupancy_rate || 0}%</span>
                </div>
                <Progress value={portfolio_summary?.occupancy_rate || 0} className="h-2" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-zinc-400">Occupancy</p>
                <Users className="w-5 h-5 text-zinc-500" />
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-3xl font-bold text-green-400">{portfolio_summary?.occupied_units || 0}</p>
                  <p className="text-xs text-zinc-500">Occupied</p>
                </div>
                <div className="text-2xl text-zinc-600">/</div>
                <div>
                  <p className="text-3xl font-bold text-zinc-400">{(portfolio_summary?.total_units || 0) - (portfolio_summary?.occupied_units || 0)}</p>
                  <p className="text-xs text-zinc-500">Vacant</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trend Chart */}
        {monthly_trend.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <BarChart3 className="w-5 h-5 text-primary" />
                Monthly Cash Flow
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {monthly_trend.map((month) => {
                  const maxValue = Math.max(...monthly_trend.map(m => Math.max(m.income, m.expenses)));
                  const incomeWidth = maxValue > 0 ? (month.income / maxValue) * 100 : 0;
                  const expenseWidth = maxValue > 0 ? (month.expenses / maxValue) * 100 : 0;
                  
                  return (
                    <div key={month.month} className="flex items-center gap-4">
                      <div className="w-20 text-xs text-zinc-500">{month.month}</div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <div 
                            className="h-4 bg-green-500/50 rounded-sm" 
                            style={{ width: `${incomeWidth}%`, minWidth: month.income > 0 ? '4px' : '0' }}
                          ></div>
                          <span className="text-xs text-green-400 font-mono">{formatCurrency(month.income)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div 
                            className="h-4 bg-red-500/50 rounded-sm" 
                            style={{ width: `${expenseWidth}%`, minWidth: month.expenses > 0 ? '4px' : '0' }}
                          ></div>
                          <span className="text-xs text-red-400 font-mono">{formatCurrency(month.expenses)}</span>
                        </div>
                      </div>
                      <div className={`w-24 text-right text-sm font-mono ${month.net >= 0 ? "text-primary" : "text-red-400"}`}>
                        {month.net >= 0 ? "+" : ""}{formatCurrency(month.net)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Income & Expense Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <TrendingUp className="w-5 h-5 text-green-400" />
                Income by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(portfolio_summary?.income_by_category || {}).length === 0 ? (
                <p className="text-zinc-500 text-sm">No income recorded</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(portfolio_summary?.income_by_category || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([cat, amount]) => {
                      const percentage = (portfolio_summary?.total_income || 0) > 0 
                        ? (amount / portfolio_summary.total_income * 100).toFixed(1) 
                        : 0;
                      return (
                        <div key={cat} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-sm text-zinc-300 capitalize">{cat.replace('_', ' ')}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-green-400 font-mono">{formatCurrencyFull(amount)}</span>
                            <span className="text-xs text-zinc-500 w-12 text-right">{percentage}%</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <TrendingDown className="w-5 h-5 text-red-400" />
                Expenses by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(portfolio_summary?.expense_by_category || {}).length === 0 ? (
                <p className="text-zinc-500 text-sm">No expenses recorded</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(portfolio_summary?.expense_by_category || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([cat, amount]) => {
                      const percentage = (portfolio_summary?.total_expenses || 0) > 0 
                        ? (amount / portfolio_summary.total_expenses * 100).toFixed(1) 
                        : 0;
                      return (
                        <div key={cat} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="text-sm text-zinc-300 capitalize">{cat.replace('_', ' ')}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-red-400 font-mono">{formatCurrencyFull(amount)}</span>
                            <span className="text-xs text-zinc-500 w-12 text-right">{percentage}%</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Property Cards */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Properties ({properties.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {properties.map((prop) => (
              <Card key={prop.id} className="bg-[#0A0A0A] border-white/10 rounded-none hover:border-white/20 transition-colors">
                <CardContent className="p-0">
                  {/* Property Header */}
                  <div className="p-4 border-b border-white/10">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-white text-lg">{prop.name}</h3>
                        <p className="text-xs text-zinc-500">{prop.address}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded capitalize">
                            {prop.property_type}
                          </span>
                          <span className="text-xs text-zinc-500">
                            {prop.occupied_units}/{prop.unit_count} units occupied
                          </span>
                        </div>
                      </div>
                      <Link to={`/real-estate/${prop.id}/ledger`}>
                        <Button variant="outline" size="sm" className="border-primary/50 text-primary hover:bg-primary/10 rounded-sm">
                          <BookOpen className="w-4 h-4 mr-1" />
                          Ledger
                        </Button>
                      </Link>
                    </div>
                  </div>
                  
                  {/* Property Financials */}
                  <div className="p-4 grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-zinc-500">Income</p>
                      <p className="text-lg font-bold text-green-400 font-mono">{formatCurrency(prop.total_income)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Expenses</p>
                      <p className="text-lg font-bold text-red-400 font-mono">{formatCurrency(prop.total_expenses)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Net Profit</p>
                      <p className={`text-lg font-bold font-mono ${prop.net_profit >= 0 ? "text-primary" : "text-red-400"}`}>
                        {formatCurrency(prop.net_profit)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Units Summary */}
                  {prop.units.length > 0 && (
                    <div className="px-4 pb-4">
                      <p className="text-xs text-zinc-500 mb-2">Units Performance</p>
                      <div className="space-y-2">
                        {prop.units.map((unit) => (
                          <div key={unit.id} className="flex items-center justify-between text-sm p-2 bg-black/30 rounded">
                            <div className="flex items-center gap-2">
                              {unit.is_occupied ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <XCircle className="w-4 h-4 text-zinc-500" />
                              )}
                              <span className="text-zinc-300">Unit {unit.unit_number}</span>
                              {unit.tenant_name && (
                                <span className="text-zinc-500 text-xs">({unit.tenant_name})</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs font-mono">
                              <span className="text-green-400">+{formatCurrency(unit.income)}</span>
                              <span className="text-red-400">-{formatCurrency(unit.expenses)}</span>
                              <span className={`font-bold ${unit.net >= 0 ? "text-primary" : "text-red-400"}`}>
                                {formatCurrency(unit.net)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Property Value Footer */}
                  <div className="px-4 py-3 bg-zinc-900/50 border-t border-white/5 flex items-center justify-between text-xs">
                    <div>
                      <span className="text-zinc-500">Value: </span>
                      <span className="text-white font-mono">{formatCurrency(prop.current_value)}</span>
                    </div>
                    {prop.has_mortgage && (
                      <div>
                        <span className="text-zinc-500">Mortgage: </span>
                        <span className="text-red-400 font-mono">{formatCurrency(prop.mortgage_balance)}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-zinc-500">Equity: </span>
                      <span className="text-blue-400 font-mono">{formatCurrency(prop.current_value - (prop.mortgage_balance || 0))}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        {/* No Properties State */}
        {properties.length === 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-lg font-bold text-white mb-2">No Properties Yet</h3>
              <p className="text-zinc-500 mb-4">Add your first property to start tracking rental income</p>
              <Link to="/real-estate">
                <Button className="bg-primary text-black rounded-sm">
                  <Building2 className="w-4 h-4 mr-2" />
                  Add Property
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
